
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@/context/ThemeContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from "@/components/ui/toaster";

import MainLayout from './components/layouts/MainLayout';
import ScrollToTop from './components/ScrollToTop';
import { SearchContext } from '@/context/SearchContext';
import { FullPageLoader } from '@/components/ui/loader';

// Eagerly loaded pages (critical paths)
import Index from './pages/Index';

// Lazily loaded pages (non-critical paths)
const TrekListing = lazy(() => import('./pages/TrekListing'));
const TrekDetail = lazy(() => import('./pages/TrekDetail'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const CompanyListing = lazy(() => import('./pages/CompanyListing'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const TrekPackageComparison = lazy(() => import('./pages/TrekPackageComparison'));
const TrekComparison = lazy(() => import('./pages/TrekComparison'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));

// Admin pages - lazy loaded
const Login = lazy(() => import('./pages/admin/Login'));
const AdminLayout = lazy(() => import('./components/layouts/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const CompaniesManagement = lazy(() => import('./pages/admin/CompaniesManagement'));
const CompanyForm = lazy(() => import('./pages/admin/forms/CompanyForm'));
const TreksManagement = lazy(() => import('./pages/admin/TreksManagement'));
const TrekForm = lazy(() => import('./pages/admin/forms/TrekForm'));
const PackagesManagement = lazy(() => import('./pages/admin/PackagesManagement'));
const PackageForm = lazy(() => import('./pages/admin/forms/PackageForm'));
const InquiriesManagement = lazy(() => import('./pages/admin/InquiriesManagement'));
const AdminsManagement = lazy(() => import('./pages/admin/AdminsManagement'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const ContentStrategyGuide = lazy(() => import("./pages/admin/ContentStrategyGuide"));
const SeoAnalytics = lazy(() => import("./pages/admin/SeoAnalytics"));

// Create loading fallbacks for different sections
const MainRouteLoader = () => <FullPageLoader text="Loading content..." />;
const AdminRouteLoader = () => <FullPageLoader text="Loading admin dashboard..." />;

const queryClient = new QueryClient();

const App = () => {
  return (
    <HelmetProvider>
      <SearchContext>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <Toaster />
            <Router>
              <ScrollToTop />
              <Routes>
                {/* Main routes with Navbar - eagerly load the homepage */}
                <Route path="/" element={<MainLayout><Index /></MainLayout>} />
                
                {/* Lazily loaded main routes */}
                <Route path="/treks" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <TrekListing />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/trek/:trekId" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <TrekDetail />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/search" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <SearchResults />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/companies" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <CompanyListing />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/company/:companyId" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <CompanyProfile />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/about" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <About />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/contact" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <Contact />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/privacy" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <Privacy />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/terms" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <Terms />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/trek/:trekId/packages" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <TrekPackageComparison />
                    </Suspense>
                  </MainLayout>
                } />
                
                {/* Admin routes */}
                <Route path="/admin/login" element={
                  <Suspense fallback={<AdminRouteLoader />}>
                    <Login />
                  </Suspense>
                } />
                
                {/* Wrap the entire admin section in a Suspense boundary */}
                <Route element={
                  <Suspense fallback={<AdminRouteLoader />}>
                    <AdminLayout />
                  </Suspense>
                }>
                  <Route path="/admin" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <Dashboard />
                    </Suspense>
                  } />
                  <Route path="/admin/companies" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <CompaniesManagement />
                    </Suspense>
                  } />
                  <Route path="/admin/companies/new" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <CompanyForm />
                    </Suspense>
                  } />
                  <Route path="/admin/companies/edit/:companyId" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <CompanyForm />
                    </Suspense>
                  } />
                  <Route path="/admin/treks" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <TreksManagement />
                    </Suspense>
                  } />
                  <Route path="/admin/treks/new" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <TrekForm />
                    </Suspense>
                  } />
                  <Route path="/admin/treks/edit/:trekId" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <TrekForm />
                    </Suspense>
                  } />
                  <Route path="/admin/packages" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <PackagesManagement />
                    </Suspense>
                  } />
                  <Route path="/admin/packages/new" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <PackageForm />
                    </Suspense>
                  } />
                  <Route path="/admin/packages/edit/:packageId" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <PackageForm />
                    </Suspense>
                  } />
                  <Route path="/admin/inquiries" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <InquiriesManagement />
                    </Suspense>
                  } />
                  <Route path="/admin/admins" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <AdminsManagement />
                    </Suspense>
                  } />
                  <Route path="/admin/settings" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <Settings />
                    </Suspense>
                  } />
                  <Route path="/admin/seo" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <SeoAnalytics />
                    </Suspense>
                  } />
                  <Route path="/admin/seo/content-strategy" element={
                    <Suspense fallback={<AdminRouteLoader />}>
                      <ContentStrategyGuide />
                    </Suspense>
                  } />
                </Route>
                
                <Route path="*" element={
                  <MainLayout>
                    <Suspense fallback={<MainRouteLoader />}>
                      <NotFound />
                    </Suspense>
                  </MainLayout>
                } />
              </Routes>
            </Router>
          </QueryClientProvider>
        </ThemeProvider>
      </SearchContext>
    </HelmetProvider>
  );
};

export default App;
